/**
 * Admin URL Redirects manager.
 *
 * Lets the admin point any incoming path at a target URL so old/short
 * links from email blasts and social posts never 404. Hit-count and
 * last-hit timestamp visible per row.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, ExternalLink, Loader2, Search, Copy, Check,
  ArrowRight, ArrowLeft, ToggleLeft, ToggleRight, Save, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { readableApiError } from '../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRedirects() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ from_path: '', target: '', note: '' });
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin') { navigate('/'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/redirects`, auth);
      setRows(res.data?.redirects || []);
    } catch (err) {
      toast.error(readableApiError(err, 'Failed to load redirects'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openCreate = () => {
    setEditing(null);
    setForm({ from_path: '', target: '', note: '' });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ from_path: row.from_path, target: row.target, note: row.note || '' });
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e?.preventDefault();
    setCreating(true);
    try {
      if (editing) {
        await axios.patch(`${API}/admin/redirects/${editing.redirect_id}`, form, auth);
        toast.success('Redirect updated');
      } else {
        await axios.post(`${API}/admin/redirects`, form, auth);
        toast.success('Redirect created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ from_path: '', target: '', note: '' });
      load();
    } catch (err) {
      toast.error(readableApiError(err, 'Save failed'));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this redirect? Old links will 404 again.')) return;
    try {
      await axios.delete(`${API}/admin/redirects/${id}`, auth);
      setRows((r) => r.filter((x) => x.redirect_id !== id));
      toast.success('Redirect removed');
    } catch (err) {
      toast.error(readableApiError(err, 'Delete failed'));
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(
        `${API}/admin/redirects/${row.redirect_id}`,
        { active: !row.active },
        auth,
      );
      load();
    } catch (err) {
      toast.error(readableApiError(err, 'Update failed'));
    }
  };

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    setCopied(key);
    setTimeout(() => setCopied(null), 1200);
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (r.from_path || '').toLowerCase().includes(s)
      || (r.target || '').toLowerCase().includes(s)
      || (r.note || '').toLowerCase().includes(s)
    );
  });

  const totalHits = rows.reduce((acc, r) => acc + (r.hit_count || 0), 0);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="admin-redirects">
      <div className="max-w-6xl mx-auto px-4 space-y-5">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3"
            data-testid="redirects-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
          </button>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-400">
                Admin · URL Redirects
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">
                Never 404 a link again
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Point any incoming path (e.g. <span className="font-mono text-foreground">/the-naked-sketch</span>) at the real URL.
                Works on every path — root, blog, anywhere.
              </p>
            </div>
            <Button
              onClick={openCreate}
              data-testid="redirect-new-btn"
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-bold"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New redirect
            </Button>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total redirects</p>
            <p className="text-3xl font-bold mt-1 tabular-nums">{rows.length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Active</p>
            <p className="text-3xl font-bold mt-1 tabular-nums text-emerald-300">{rows.filter((r) => r.active).length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Lifetime hits</p>
            <p className="text-3xl font-bold mt-1 tabular-nums text-fuchsia-300">{totalHits.toLocaleString()}</p>
          </CardContent></Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by from-path, target or note…"
            className="pl-9"
            data-testid="redirects-search"
          />
        </div>

        {/* Form (modal-ish inline) */}
        {showForm && (
          <Card className="border-indigo-500/40 bg-indigo-500/5">
            <CardContent className="p-5">
              <form onSubmit={submitForm} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold flex items-center gap-2">
                    {editing ? 'Edit redirect' : 'New redirect'}
                  </h2>
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setShowForm(false); setEditing(null); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">From path</Label>
                    <Input
                      value={form.from_path}
                      onChange={(e) => setForm((f) => ({ ...f, from_path: e.target.value }))}
                      placeholder="/the-naked-sketch"
                      data-testid="redirect-from-input"
                      required
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Any path. Leading <span className="font-mono">/</span> is added automatically. Case-insensitive.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Target URL</Label>
                    <Input
                      value={form.target}
                      onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                      placeholder="/events/denver/the-naked-sketch"
                      data-testid="redirect-target-input"
                      required
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Internal path or full <span className="font-mono">https://…</span> URL.
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Note (internal)</Label>
                  <Input
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="e.g. Mar 24 email blast to ticket-holders"
                    data-testid="redirect-note-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowForm(false); setEditing(null); }}
                    data-testid="redirect-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="flex-1 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-bold"
                    data-testid="redirect-save-btn"
                  >
                    {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {editing ? 'Save changes' : 'Create redirect'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Rows */}
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
            {search ? 'No redirects match your search.' : (
              <>No redirects yet. <button onClick={openCreate} className="underline text-indigo-300">Create your first one →</button></>
            )}
          </CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="redirects-list">
            {filtered.map((r) => (
              <Card
                key={r.redirect_id}
                className={r.active ? 'border-border' : 'border-border bg-card/40 opacity-60'}
                data-testid={`redirect-row-${r.redirect_id}`}
              >
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold">{r.from_path}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-mono text-sm text-indigo-300 truncate">{r.target}</span>
                      <button
                        onClick={() => copy(`${window.location.origin}${r.from_path}`, r.redirect_id)}
                        title="Copy public link"
                        className="text-muted-foreground hover:text-foreground"
                        data-testid={`redirect-copy-${r.redirect_id}`}
                      >
                        {copied === r.redirect_id ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                    {r.note && <p className="text-xs text-muted-foreground mt-1">{r.note}</p>}
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                      <Badge className={r.active ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-muted text-muted-foreground'}>
                        {r.active ? 'Active' : 'Disabled'}
                      </Badge>
                      <span>{(r.hit_count || 0).toLocaleString()} hits</span>
                      {r.last_hit_at && <span>· last hit {new Date(r.last_hit_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleActive(r)}
                      className="p-1.5 rounded hover:bg-card text-muted-foreground hover:text-foreground"
                      title={r.active ? 'Disable' : 'Enable'}
                      data-testid={`redirect-toggle-${r.redirect_id}`}
                    >
                      {r.active ? <ToggleRight className="h-5 w-5 text-emerald-400" /> : <ToggleLeft className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={() => window.open(/^https?:\/\//i.test(r.target) ? r.target : r.from_path, '_blank')}
                      className="p-1.5 rounded hover:bg-card text-muted-foreground hover:text-foreground"
                      title="Test redirect"
                      data-testid={`redirect-test-${r.redirect_id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEdit(r)}
                      className="px-2 py-1.5 text-xs rounded hover:bg-card text-muted-foreground hover:text-foreground"
                      data-testid={`redirect-edit-${r.redirect_id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(r.redirect_id)}
                      className="p-1.5 rounded hover:bg-destructive/10 text-destructive"
                      title="Delete"
                      data-testid={`redirect-delete-${r.redirect_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
